/**
 * Signed-in session state: tokens, decoded claims, silent refresh and sign-out.
 *
 * The provider is the only place that writes session storage, so every screen
 * reads one source of truth and a browser refresh restores the session.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import type { ApiClient, MfaVerification, TokenResponse } from '../lib/api-client';
import { STORAGE_KEYS } from '../lib/constants';
import { decodeAccessToken, type AccessTokenClaims } from '../lib/jwt';
import type { Persona } from '../lib/personas';
import {
  loadSession,
  saveSession,
  writePreference,
  type StoredSession,
} from '../lib/session-storage';

/** Refresh this far ahead of expiry so a request never races the clock. */
const REFRESH_LEAD_MS = 60_000;

/**
 * The team a Fanatic is currently looking at. A Fanatic access token carries no
 * `tid` claim (they belong to the shared `public-fanatics` realm, not to a
 * team), so this selection — not the token — is what resolves the
 * `X-Tenant-ID` header the gateway checks the grant against.
 */
export interface ActiveTenantContext {
  tenantId: string;
  slug: string | null;
  name: string | null;
}

function loadActiveTenant(): ActiveTenantContext | null {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEYS.fanaticTeam);
    if (raw === null || raw === undefined) return null;
    const parsed = JSON.parse(raw) as Partial<ActiveTenantContext>;
    if (typeof parsed?.tenantId !== 'string' || parsed.tenantId === '') return null;
    return {
      tenantId: parsed.tenantId,
      slug: typeof parsed.slug === 'string' ? parsed.slug : null,
      name: typeof parsed.name === 'string' ? parsed.name : null,
    };
  } catch {
    return null;
  }
}

function saveActiveTenant(next: ActiveTenantContext | null): void {
  try {
    if (next === null) globalThis.localStorage?.removeItem(STORAGE_KEYS.fanaticTeam);
    else globalThis.localStorage?.setItem(STORAGE_KEYS.fanaticTeam, JSON.stringify(next));
  } catch {
    // Private-mode storage denial must never break team switching.
  }
}

/**
 * The team codes the current session may open as `/app/{slug}/…`.
 *
 * Tenant personas (staff, Team Admin, Platform Admin) sign in to exactly one
 * team: the slug captured at login, whose tenant id is the token's `tid`
 * claim — the tenant every API call is scoped to server-side. A Fanatic's
 * token has no `tid`; the team they have selected from their grants
 * (`activeTenant`) is the only one their `X-Tenant-ID` header names. Shaped
 * as a list so a future multi-team membership only has to add entries here.
 *
 * Slugs are compared case-insensitively (`TENANT_SLUG_PATTERN` is lower-case;
 * the URL bar is not) and returned lower-cased.
 */
export function accessibleTenantSlugsFor(
  session: Pick<StoredSession, 'tenantSlug'> | null,
  persona: Persona | null,
  activeTenant: Pick<ActiveTenantContext, 'slug'> | null
): readonly string[] {
  if (session === null || persona === null) return [];
  if (persona === 'fanatic') {
    return activeTenant?.slug ? [activeTenant.slug.toLowerCase()] : [];
  }
  return session.tenantSlug ? [session.tenantSlug.toLowerCase()] : [];
}

export interface SessionContextValue {
  session: StoredSession | null;
  claims: AccessTokenClaims | null;
  persona: Persona | null;
  tenantSlug: string | null;
  /** Team codes this session may open as `/app/{slug}` — see {@link accessibleTenantSlugsFor}. */
  accessibleTenantSlugs: readonly string[];
  /**
   * Whether `/app/{slug}` belongs to this session. The router refuses to mount
   * the tenant shell for any other slug (docs/TENANT_ISOLATION.md, "URL slug
   * validation"): the API scopes every call to the token's tenant, so chrome
   * labelled with a foreign slug would be showing this session's data under
   * another team's name.
   */
  canAccessTenantSlug: (slug: string) => boolean;
  isAuthenticated: boolean;
  /** True when the persona needs MFA and has not presented a code yet. */
  isMfaPending: boolean;
  /** Restoring/refreshing a stored session on first paint. */
  isBootstrapping: boolean;
  /** Fanatic-only team context; `null` for tenant personas (their token has `tid`). */
  activeTenant: ActiveTenantContext | null;
  /** Switch the Fanatic's team context; persisted so a refresh restores it. */
  setActiveTenant: (next: ActiveTenantContext | null) => void;
  startSession: (tokens: TokenResponse, tenantSlug: string | null) => StoredSession;
  applyMfaVerification: (verification: MfaVerification) => void;
  signOut: (options?: { isRemote?: boolean }) => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

function toStoredSession(
  tokens: TokenResponse,
  tenantSlug: string | null,
  nowMs: number
): StoredSession {
  return {
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
    tenantId: tokens.tenant_id,
    tenantSlug,
    persona: tokens.persona,
    expiresAtMs: nowMs + tokens.expires_in * 1000,
    isMfaSatisfied: !tokens.mfa_required,
    isMfaEnrolled: tokens.mfa_enrolled,
  };
}

export function SessionProvider({
  apiClient,
  children,
}: {
  apiClient: ApiClient;
  children: ReactNode;
}): JSX.Element {
  const [session, setSession] = useState<StoredSession | null>(() => loadSession());
  const [isBootstrapping, setIsBootstrapping] = useState(() => loadSession() !== null);
  const [activeTenant, setActiveTenantState] = useState<ActiveTenantContext | null>(() =>
    loadActiveTenant()
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persist = useCallback((next: StoredSession | null) => {
    saveSession(next);
    setSession(next);
    if (next === null) {
      writePreference(`${STORAGE_KEYS.session}.refresh-attempt`, null);
      saveActiveTenant(null);
      setActiveTenantState(null);
    }
  }, []);

  // Storage events do not fire in the writer; persist updates that tab directly.
  // Read current storage rather than event.newValue: queued events can be stale.
  useEffect(() => {
    const synchronize = () => {
      const next = loadSession();
      setSession((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
      setActiveTenantState(loadActiveTenant());
    };
    const onStorage = (event: StorageEvent) => {
      if (
        event.key === null ||
        event.key === STORAGE_KEYS.session ||
        event.key === STORAGE_KEYS.fanaticTeam
      ) {
        synchronize();
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('pageshow', synchronize);
    window.addEventListener('focus', synchronize);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('pageshow', synchronize);
      window.removeEventListener('focus', synchronize);
    };
  }, []);

  const setActiveTenant = useCallback((next: ActiveTenantContext | null) => {
    saveActiveTenant(next);
    setActiveTenantState(next);
  }, []);

  const startSession = useCallback(
    (tokens: TokenResponse, tenantSlug: string | null) => {
      const next = toStoredSession(tokens, tenantSlug, Date.now());
      persist(next);
      return next;
    },
    [persist]
  );

  const applyMfaVerification = useCallback((verification: MfaVerification) => {
    setSession((current) => {
      if (current === null) return current;
      const next: StoredSession = {
        ...current,
        accessToken: verification.access_token,
        expiresAtMs: Date.now() + verification.expires_in * 1000,
        isMfaSatisfied: verification.mfa_verified,
        isMfaEnrolled: verification.mfa_enrolled,
      };
      saveSession(next);
      return next;
    });
  }, []);

  const signOut = useCallback(
    async (options?: { isRemote?: boolean }) => {
      const current = loadSession();
      persist(null);
      if (options?.isRemote === false || current === null) return;
      try {
        await apiClient.revokeRefreshToken(current.refreshToken);
      } catch {
        // Local sign-out already happened; a stale server session expires on its own.
      }
    },
    [apiClient, persist]
  );

  // Silent refresh: renew shortly before expiry, and immediately when a stored
  // session is already past it (tab was closed over halftime).
  useEffect(() => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    if (session === null) {
      setIsBootstrapping(false);
      return;
    }
    const locks = navigator.locks;
    // A localStorage lease is not an atomic lock (and stealing an expired lease
    // can replay a token still in flight). Fail closed on unsupported browsers:
    // use the existing access token until expiry, then require a fresh login.
    const delay = Math.max(0, session.expiresAtMs - Date.now() - (locks ? REFRESH_LEAD_MS : 0));
    let isCancelled = false;

    const refreshWhenDue = () => {
      if (!locks) {
        const current = loadSession();
        if (current?.refreshToken === session.refreshToken) persist(null);
        else setSession(current);
        setIsBootstrapping(false);
        return;
      }
      void locks
        .request(`${STORAGE_KEYS.session}.refresh`, async () => {
          if (isCancelled) return;
          const current = loadSession();
          if (current?.refreshToken !== session.refreshToken) {
            setSession(current);
            return;
          }
          try {
            // Web Locks release on tab death, even if the server consumed the
            // request. Persist send-intent before the network call; a subsequent
            // owner must require login, never retry that ambiguous token. Keep the
            // marker until the next generation replaces it (no lease stealing).
            const attemptKey = `${STORAGE_KEYS.session}.refresh-attempt`;
            if (localStorage.getItem(attemptKey) === current.refreshToken) {
              persist(null);
              return;
            }
            // Check under the lock, immediately before send-intent: connectivity
            // may have changed while this tab waited for another owner. Only an
            // UNSENT refresh can wait for reconnect. A prior intent above, or any
            // failure after dispatch below, remains ambiguous and fails closed.
            if (navigator.onLine === false) return;
            localStorage.setItem(attemptKey, current.refreshToken);
            const tokens = await apiClient.refresh(current.refreshToken);
            const next = toStoredSession(tokens, current.tenantSlug, Date.now());
            next.isMfaSatisfied = current.isMfaSatisfied && !tokens.mfa_required;
            if (loadSession()?.refreshToken === current.refreshToken) persist(next);
            else setSession(loadSession());
          } catch {
            // An old request cannot erase a newer login or resurrect a logout.
            if (loadSession()?.refreshToken === current.refreshToken) persist(null);
            else setSession(loadSession());
          } finally {
            if (!isCancelled) setIsBootstrapping(false);
          }
        })
        .catch(() => {
          // Lock denial is not permission to rotate without coordination.
          if (isCancelled) return;
          const current = loadSession();
          if (current?.refreshToken === session.refreshToken) persist(null);
          else setSession(current);
          setIsBootstrapping(false);
        });
    };
    timerRef.current = setTimeout(refreshWhenDue, delay);
    const onOnline = () => {
      if (navigator.onLine === false) return;
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      const remaining = Math.max(
        0,
        session.expiresAtMs - Date.now() - (locks ? REFRESH_LEAD_MS : 0)
      );
      timerRef.current = setTimeout(refreshWhenDue, remaining);
    };
    window.addEventListener('online', onOnline);

    if (delay > 0) setIsBootstrapping(false);

    return () => {
      isCancelled = true;
      window.removeEventListener('online', onOnline);
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [apiClient, persist, session]);

  const value = useMemo<SessionContextValue>(() => {
    const claims = session === null ? null : decodeAccessToken(session.accessToken);
    const persona = claims?.persona ?? session?.persona ?? null;
    // MFA is per account, not per persona: the auth service already folded
    // `users.mfa_enforced` into `mfa_required` on the token response, and
    // `isMfaSatisfied` is its negation. Defence in depth: the access token's
    // own `mfa_required` / `mfa_verified` claims are what the gateway gates
    // on, so a PARTIAL token is treated as pending even if the response body
    // (or an OAuth callback's URL fragment) was tampered with or lost the flag.
    // The route guards (`router.tsx`, `tenant-router.tsx`) redirect pending
    // sessions back to sign-in, so no partial token reaches `/app/{slug}`.
    const isMfaPending =
      session !== null &&
      (!session.isMfaSatisfied ||
        (claims !== null && claims.isMfaRequired && !claims.isMfaVerified));
    const fanaticTenant = persona === 'fanatic' ? activeTenant : null;
    const accessibleTenantSlugs = accessibleTenantSlugsFor(session, persona, fanaticTenant);
    return {
      session,
      claims,
      persona,
      tenantSlug: session?.tenantSlug ?? null,
      accessibleTenantSlugs,
      canAccessTenantSlug: (slug: string) =>
        accessibleTenantSlugs.includes(slug.trim().toLowerCase()),
      isAuthenticated: session !== null,
      isMfaPending,
      isBootstrapping,
      activeTenant: fanaticTenant,
      setActiveTenant,
      startSession,
      applyMfaVerification,
      signOut,
    };
  }, [
    activeTenant,
    applyMfaVerification,
    isBootstrapping,
    session,
    setActiveTenant,
    signOut,
    startSession,
  ]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const value = useContext(SessionContext);
  if (value === null) {
    throw new SessionContextError();
  }
  return value;
}

class SessionContextError extends Error {
  constructor() {
    super('useSession() must be called inside <SessionProvider>.');
    this.name = 'SessionContextError';
  }
}
